// Editor de código con CodeMirror 6: C++, autocompletado de la API de Arduino y errores en línea.
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter, drawSelection, dropCursor, rectangularSelection, crosshairCursor, highlightSpecialChars } from '@codemirror/view';
import { EditorState, Compartment } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { cpp } from '@codemirror/lang-cpp';
import { oneDark } from '@codemirror/theme-one-dark';
import { autocompletion, completionKeymap, closeBrackets, closeBracketsKeymap, snippetCompletion } from '@codemirror/autocomplete';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { lintGutter, setDiagnostics } from '@codemirror/lint';
import { bracketMatching, indentOnInput, foldGutter, foldKeymap, indentUnit } from '@codemirror/language';

const API = [
  ['pinMode(pin, modo)', 'pinMode(${pin}, ${OUTPUT})', 'Configura un pin como INPUT, OUTPUT o INPUT_PULLUP.'],
  ['digitalWrite(pin, valor)', 'digitalWrite(${pin}, ${HIGH})', 'Pone un pin de salida en HIGH (5 V) o LOW (0 V).'],
  ['digitalRead(pin)', 'digitalRead(${pin})', 'Lee un pin digital: HIGH o LOW.'],
  ['analogRead(pin)', 'analogRead(${A0})', 'Lee una entrada analógica: 0 a 1023 (0 a 5 V).'],
  ['analogWrite(pin, valor)', 'analogWrite(${pin}, ${128})', 'PWM de 0 a 255 en los pines con ~ (3, 5, 6, 9, 10, 11).'],
  ['analogReference(tipo)', 'analogReference(${DEFAULT})', 'Referencia del ADC: DEFAULT, INTERNAL o EXTERNAL.'],
  ['delay(ms)', 'delay(${1000})', 'Pausa el programa los milisegundos indicados.'],
  ['delayMicroseconds(us)', 'delayMicroseconds(${10})', 'Pausa en microsegundos.'],
  ['millis()', 'millis()', 'Milisegundos desde que arrancó el programa.'],
  ['micros()', 'micros()', 'Microsegundos desde que arrancó el programa.'],
  ['tone(pin, frecuencia, duración)', 'tone(${pin}, ${440})', 'Genera una onda cuadrada (sonido) en el pin.'],
  ['noTone(pin)', 'noTone(${pin})', 'Detiene el sonido de tone().'],
  ['pulseIn(pin, valor, espera)', 'pulseIn(${pin}, ${HIGH})', 'Mide la duración de un pulso en microsegundos.'],
  ['shiftOut(datos, reloj, orden, valor)', 'shiftOut(${dataPin}, ${clockPin}, ${MSBFIRST}, ${valor})', 'Envía un byte bit a bit (registros de desplazamiento).'],
  ['shiftIn(datos, reloj, orden)', 'shiftIn(${dataPin}, ${clockPin}, ${MSBFIRST})', 'Lee un byte bit a bit.'],
  ['attachInterrupt(interrupción, función, modo)', 'attachInterrupt(digitalPinToInterrupt(${2}), ${funcion}, ${FALLING})', 'Llama a una función cuando cambia un pin (2 o 3 en el Uno).'],
  ['detachInterrupt(interrupción)', 'detachInterrupt(digitalPinToInterrupt(${2}))', 'Quita una interrupción.'],
  ['map(valor, de_min, de_max, a_min, a_max)', 'map(${valor}, ${0}, ${1023}, ${0}, ${255})', 'Cambia un número de un rango a otro.'],
  ['constrain(x, mín, máx)', 'constrain(${x}, ${0}, ${255})', 'Limita un valor a un rango.'],
  ['random(mín, máx)', 'random(${0}, ${100})', 'Número pseudoaleatorio.'],
  ['randomSeed(semilla)', 'randomSeed(${analogRead(A0)})', 'Inicializa el generador aleatorio.'],
  ['Serial.begin(baudios)', 'Serial.begin(${9600})', 'Inicia el puerto serie (monitor serie).'],
  ['Serial.print(valor)', 'Serial.print(${valor})', 'Escribe en el monitor serie.'],
  ['Serial.println(valor)', 'Serial.println(${valor})', 'Escribe en el monitor serie y salta de línea.'],
  ['Serial.available()', 'Serial.available()', 'Cantidad de bytes recibidos por leer.'],
  ['Serial.read()', 'Serial.read()', 'Lee un byte recibido (-1 si no hay).'],
  ['Serial.parseInt()', 'Serial.parseInt()', 'Lee un número entero de lo recibido.'],
  ['Serial.readStringUntil(c)', "Serial.readStringUntil('${\\n}')", 'Lee texto hasta un carácter.'],
  ['Serial.write(byte)', 'Serial.write(${b})', 'Envía un byte.'],
];

const WORDS = ['HIGH', 'LOW', 'INPUT', 'OUTPUT', 'INPUT_PULLUP', 'LED_BUILTIN', 'A0', 'A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'true', 'false', 'MSBFIRST', 'LSBFIRST', 'RISING', 'FALLING', 'CHANGE', 'DEFAULT', 'INTERNAL', 'EXTERNAL', 'byte', 'word', 'boolean', 'bool', 'String', 'unsigned', 'long', 'int', 'float', 'double', 'char', 'void', 'const', 'volatile', 'static', 'uint8_t', 'uint16_t', 'uint32_t', 'int8_t', 'int16_t', 'int32_t', 'setup', 'loop'];

// Solo con la Raspberry Pi Pico (núcleo arduino-pico).
const PICO_API = [
  ['analogReadResolution(bits)', 'analogReadResolution(${12})', 'Bits de analogRead: 10 por omisión; el ADC del RP2040 da hasta 12 (0–4095).'],
  ['analogReadTemp()', 'analogReadTemp()', 'Temperatura del chip en °C (sensor interno del RP2040).'],
  ['analogWriteFreq(hz)', 'analogWriteFreq(${1000})', 'Frecuencia del PWM de analogWrite (1 kHz por omisión).'],
  ['analogWriteRange(máximo)', 'analogWriteRange(${255})', 'Valor máximo de analogWrite (255 por omisión).'],
  ['Serial1.begin(baudios)', 'Serial1.begin(${9600})', 'UART0 en GP0 (TX) y GP1 (RX).'],
  ['Serial2.begin(baudios)', 'Serial2.begin(${9600})', 'UART1 en GP8 (TX) y GP9 (RX).'],
  ['Wire1.begin()', 'Wire1.begin()', 'Segundo bus I²C: GP26 (SDA) y GP27 (SCL).'],
];
const PICO_WORDS = ['BOOTSEL', 'INPUT_PULLDOWN', 'A3'];

function arduinoCompletions(app) {
  return (ctx) => {
    const word = ctx.matchBefore(/[\w.]+/);
    if (!word || (word.from === word.to && !ctx.explicit)) return null;
    const pico = app.boardType() === 'pi-pico';
    return {
      from: word.from,
      options: [
        ...[...API, ...(pico ? PICO_API : [])].map(([label, snippet, info]) => snippetCompletion(snippet, { label: label.split('(')[0], detail: label.slice(label.indexOf('(')), info, type: 'function' })),
        ...[...WORDS, ...(pico ? PICO_WORDS : [])].map((w) => ({ label: w, type: /^[A-Z_0-9]+$/.test(w) ? 'constant' : 'keyword' })),
      ],
      validFor: /^[\w.]*$/,
    };
  };
}

const theme = EditorView.theme({
  '&': { backgroundColor: '#1e2329' },
  '.cm-gutters': { backgroundColor: '#1a1f25', borderRight: '1px solid #29333f', color: '#5c6878' },
  '.cm-activeLineGutter': { backgroundColor: '#232a33' },
  '.cm-activeLine': { backgroundColor: 'rgba(255,255,255,0.035)' },
  '.cm-tooltip': { border: '1px solid #344050', backgroundColor: '#1b222b' },
  '.cm-tooltip-autocomplete > ul > li[aria-selected]': { backgroundColor: '#0b7d8e' },
  '.cm-diagnostic': { fontFamily: 'var(--font)' },
});

export class CodeEditor {
  constructor(app, el, tabsEl) {
    this.app = app;
    this.el = el;
    this.tabsEl = tabsEl;
    this.states = new Map();
    this.current = 'sketch.ino';
    this.readOnly = new Compartment();
    this.ro = !!app.readOnly; // solo lectura: se puede leer, seleccionar y copiar, pero no escribir
    this.view = new EditorView({ parent: el, state: this.makeState('') });
    app.on('project-loaded', () => this.loadProject());
    app.on('read-only', (reason) => this.setReadOnly(!!reason));
    app.on('files', () => this.renderTabs());
    app.on('compile', (res) => this.showDiagnostics(res));
  }

  extensions() {
    return [
      lineNumbers(),
      highlightActiveLineGutter(),
      highlightSpecialChars(),
      history(),
      foldGutter(),
      drawSelection(),
      dropCursor(),
      EditorState.allowMultipleSelections.of(true),
      indentOnInput(),
      indentUnit.of('  '),
      bracketMatching(),
      closeBrackets(),
      autocompletion({ override: [arduinoCompletions(this.app)], activateOnTyping: true }),
      rectangularSelection(),
      crosshairCursor(),
      highlightActiveLine(),
      highlightSelectionMatches(),
      lintGutter(),
      keymap.of([...closeBracketsKeymap, ...defaultKeymap, ...searchKeymap, ...historyKeymap, ...foldKeymap, ...completionKeymap, indentWithTab]),
      cpp(),
      oneDark,
      theme,
      this.readOnly.of(this.roExtension()),
      EditorView.updateListener.of((u) => {
        if (u.docChanged) {
          this.app.setFile(this.current, u.state.doc.toString());
          this.states.set(this.current, u.state);
        }
      }),
    ];
  }

  roExtension() {
    return this.ro ? [EditorState.readOnly.of(true)] : [];
  }

  setReadOnly(on) {
    if (this.ro === on) return;
    this.ro = on;
    const effects = this.readOnly.reconfigure(this.roExtension());
    // Cada archivo guarda su propio estado: se cambian todos, no solo el visible.
    for (const [name, st] of this.states) if (name !== this.current) this.states.set(name, st.update({ effects }).state);
    this.view.dispatch({ effects });
    this.states.set(this.current, this.view.state);
    this.el.classList.toggle('ro', on);
    // Puede llegar antes del primer proyecto (al abrir uno ajeno): las pestañas se pintan al cargarlo.
    if (this.app.project) this.renderTabs();
  }

  makeState(doc) {
    return EditorState.create({ doc, extensions: this.extensions() });
  }

  loadProject() {
    this.states.clear();
    const files = this.app.project.files;
    for (const [name, content] of Object.entries(files)) this.states.set(name, this.makeState(content));
    this.current = files['sketch.ino'] !== undefined ? 'sketch.ino' : Object.keys(files)[0];
    this.view.setState(this.states.get(this.current));
    this.renderTabs();
  }

  open(name) {
    if (!this.app.project.files[name] && this.app.project.files[name] !== '') return;
    this.states.set(this.current, this.view.state);
    this.current = name;
    if (!this.states.has(name)) this.states.set(name, this.makeState(this.app.project.files[name]));
    this.view.setState(this.states.get(name));
    this.renderTabs();
    if (this.lastDiagnostics) this.showDiagnostics(this.lastDiagnostics);
  }

  renderTabs() {
    const files = Object.keys(this.app.project.files);
    files.sort((a, b) => (a === 'sketch.ino' ? -1 : b === 'sketch.ino' ? 1 : a.localeCompare(b)));
    this.tabsEl.innerHTML = '';
    for (const name of files) {
      const b = document.createElement('button');
      b.className = 'file-tab' + (name === this.current ? ' active' : '');
      b.textContent = name;
      b.title = name === 'sketch.ino' ? 'Archivo principal del programa' : name;
      b.onclick = () => this.open(name);
      if (name !== 'sketch.ino' && !this.ro) {
        const x = document.createElement('span');
        x.className = 'x';
        x.textContent = '×';
        x.title = 'Eliminar archivo';
        x.onclick = (e) => {
          e.stopPropagation();
          if (!confirm(`¿Eliminar ${name}?`)) return;
          this.states.delete(name);
          if (this.current === name) this.current = 'sketch.ino';
          this.app.removeFile(name);
          this.view.setState(this.states.get(this.current) || this.makeState(this.app.project.files[this.current]));
          this.renderTabs();
        };
        b.appendChild(x);
      }
      this.tabsEl.appendChild(b);
    }
    if (this.ro) return;
    const add = document.createElement('button');
    add.className = 'file-tab add';
    add.textContent = '+';
    add.title = 'Agregar un archivo (.h o .cpp)';
    add.onclick = () => {
      const name = prompt('Nombre del archivo nuevo (por ejemplo, notas.h o motor.cpp):', 'config.h');
      if (!name) return;
      if (!/^[A-Za-z0-9_][A-Za-z0-9_.-]{0,90}\.(h|hpp|cpp|c)$/.test(name)) {
        this.app.toast('Usa un nombre terminado en .h, .hpp, .c o .cpp, sin espacios.', true);
        return;
      }
      if (!this.app.addFile(name)) this.app.toast('Ya existe un archivo con ese nombre.', true);
      else this.open(name);
    };
    this.tabsEl.appendChild(add);
  }

  showDiagnostics(res) {
    this.lastDiagnostics = res;
    const doc = this.view.state.doc;
    const list = (res && res.diagnostics ? res.diagnostics : [])
      .filter((d) => d.file === this.current && d.severity !== 'info' && d.line >= 1 && d.line <= doc.lines)
      .map((d) => {
        const line = doc.line(d.line);
        const from = Math.min(line.to, line.from + Math.max(0, d.col - 1));
        return { from, to: Math.max(from, line.to), severity: d.severity, message: d.message };
      });
    this.view.dispatch(setDiagnostics(this.view.state, list));
  }

  goTo(file, line, col = 1) {
    if (file !== this.current && this.app.project.files[file] !== undefined) this.open(file);
    const doc = this.view.state.doc;
    if (line < 1 || line > doc.lines) return;
    const l = doc.line(line);
    const pos = Math.min(l.to, l.from + Math.max(0, col - 1));
    this.view.dispatch({ selection: { anchor: pos }, scrollIntoView: true });
    this.view.focus();
  }

  clearDiagnostics() {
    this.lastDiagnostics = null;
    this.view.dispatch(setDiagnostics(this.view.state, []));
  }
}
